import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

/**
 * Open a link in a way that works in both web and Capacitor environments.
 *
 * - Web: opens in a new tab via window.open (default browser behavior).
 * - Capacitor (iOS/Android): opens in an in-app browser (SFSafariViewController
 *   on iOS, Chrome Custom Tabs on Android). The user can dismiss back to the
 *   app via the native "Done" button without losing in-flight form state.
 *
 * `<a target="_blank">` is silently ignored inside Capacitor WKWebView, so any
 * link the user must be able to read (terms, privacy, external help docs)
 * needs this helper.
 */
export async function openExternalLink(href: string): Promise<void> {
  // Resolve relative app paths to absolute URLs so Capacitor Browser can open them.
  const url =
    href.startsWith("http://") || href.startsWith("https://")
      ? href
      : `${window.location.origin}${href.startsWith("/") ? "" : "/"}${href}`;

  if (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable("Browser")
  ) {
    try {
      await Browser.open({ url });
      return;
    } catch (error) {
      console.warn("[openExternalLink] Browser.open failed, falling back", error);
      // fall through to window.open
    }
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * onClick handler for anchor tags that delegates to openExternalLink.
 *
 * Prevents the default <a> navigation so the WebView doesn't try and fail to
 * handle target="_blank" itself, and stops propagation so wrapping click
 * handlers (e.g. checkbox labels, modal overlays) don't fire.
 */
export function handleExternalLinkClick(href: string) {
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void openExternalLink(href);
  };
}
